import { useMemo } from 'react';
import { Marker, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import { useTrack } from '../context/TrackContext';

interface Incident {
    id: string;
    type: 'bovin' | 'eboulement' | 'promeneur' | 'autre';
    pk: number;
    timestamp: number;
    description: string;
}

interface IncidentsLayerProps {
    incidents: Incident[];
}

const INCIDENT_ICONS: Record<string, string> = {
    bovin: '🐄',
    eboulement: '⚠️',
    promeneur: '🚶',
    autre: '❓'
};

export default function IncidentsLayer({ incidents }: IncidentsLayerProps) {
    const { balagneSystem, centraleSystem } = useTrack();

    const incidentMarkers = useMemo(() => {
        return incidents.map(incident => {
            const pk = incident.pk;
            let coord: [number, number] | null = null;
            
            // Heuristic to choose the right system based on PK bounds
            if (pk >= 46.7 && pk <= 119.9) {
                coord = balagneSystem.pkToCoord(pk);
            } else if (pk >= 0 && pk <= 157.4) {
                coord = centraleSystem.pkToCoord(pk);
            } else {
                coord = balagneSystem.pkToCoord(pk) || centraleSystem.pkToCoord(pk);
            }

            return { incident, coord };
        }).filter(m => m.coord !== null && !isNaN(m.coord[0]) && !isNaN(m.coord[1]));
    }, [incidents, balagneSystem, centraleSystem]);

    return (
        <>
            {incidentMarkers.map(({ incident, coord }) => (
                <Marker
                    key={incident.id}
                    position={coord!}
                    icon={L.divIcon({
                        className: 'incident-marker',
                        html: `<div class="flex items-center justify-center w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full border-2 border-red-500 shadow-lg text-lg transform -translate-y-2">
                                ${INCIDENT_ICONS[incident.type]}
                               </div>`
                    })}
                >
                    <Tooltip sticky>
                        <div className="font-sans text-xs">
                            <strong className="text-red-600 block mb-1 uppercase">${incident.type === 'bovin' ? 'Bovins sur la voie' : incident.type.toUpperCase()}</strong>
                            <p className="font-bold text-slate-700">PK {incident.pk.toFixed(3)}</p>
                            <p className="italic text-slate-500 mt-1">« ${incident.description} »</p>
                            <p className="text-[9px] text-slate-400 mt-1 uppercase font-bold">${new Date(incident.timestamp).toLocaleTimeString()}</p>
                        </div>
                    </Tooltip>
                </Marker>
            ))}
        </>
    );
}
